import React, { useState } from 'react';
import { ActiveUser } from '../../hooks/useCollaboration';
import { SaveStatus } from '../../hooks/useCollaboration';
import { useTranslation } from 'react-i18next';
import { Check, Clock, Save, AlertCircle, RefreshCw } from 'lucide-react';

interface ActiveUsersBarProps {
  activeUsers: ActiveUser[];
  currentUserId: string;
  saveStatus: SaveStatus;
  isConnected: boolean;
}

const SaveStatusIndicator: React.FC<{ status: SaveStatus }> = ({ status }) => {
  const { t } = useTranslation();

  if (status === 'idle') return null;

  const config: Record<SaveStatus, { icon: React.ReactNode; label: string; className: string; dotColor: string }> = {
    idle: { icon: null, label: '', className: '', dotColor: '' },
    pending: {
      icon: <Clock size={12} />,
      label: t('features.swimlane.autoSavePending'),
      className: 'text-gray-400',
      dotColor: '#9ca3af',
    },
    saving: {
      icon: (
        <svg width="12" height="12" viewBox="0 0 12 12" className="animate-spin">
          <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="7 7" />
        </svg>
      ),
      label: t('features.swimlane.autoSaveSaving'),
      className: 'text-blue-500',
      dotColor: '#3b82f6',
    },
    saved: {
      icon: <Check size={12} strokeWidth={2.5} />,
      label: t('features.swimlane.autoSaveSaved'),
      className: 'text-green-600',
      dotColor: '#16a34a',
    },
    error: {
      icon: <AlertCircle size={12} />,
      label: t('features.swimlane.autoSaveError'),
      className: 'text-red-500',
      dotColor: '#ef4444',
    },
  };

  const { icon, label, className } = config[status];

  return (
    <div
      className={`flex items-center gap-1.5 text-xs font-medium transition-all duration-300 ${className}`}
      title={label}
    >
      {icon}
      <span className="hidden md:inline whitespace-nowrap">{label}</span>
    </div>
  );
};

interface UserAvatarProps {
  user: ActiveUser;
  isCurrentUser?: boolean;
  size?: number;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ user, isCurrentUser = false, size = 30 }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: user.color,
        fontSize: size * 0.38,
        border: isCurrentUser ? '2px solid var(--tenant-primary, #017d7c)' : '2px solid white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }}
      // title={user.fullname}
    >
      {user.avatar && !imgError ? (
        <img
          src={user.avatar}
          alt={user.initials}
          className="w-full h-full rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        user.initials
      )}
    </div>
  );
};

const ActiveUsersBar: React.FC<ActiveUsersBarProps> = ({
  activeUsers,
  currentUserId,
  saveStatus,
  isConnected,
}) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  // Les autres participants (on sépare le user courant des autres)
  const otherUsers = activeUsers.filter(u => u.user_id !== currentUserId);
  const currentUser = activeUsers.find(u => u.user_id === currentUserId);
  const visibleUsers = otherUsers.slice(0, 4);
  const overflowCount = Math.max(0, otherUsers.length - 4);

  return (
    <div className="flex items-center gap-3">
      {/* Indicateur statut sauvegarde */}
      <SaveStatusIndicator status={saveStatus} />

      {/* Indicateur connexion WebSocket */}
      <div
        className="flex items-center gap-1"
        title={isConnected
          ? (t('features.swimlane.wsConnected') || 'Automatic saving activated')
          : (t('features.swimlane.wsDisconnected') || 'Disconnected')}
      >
        <RefreshCw
          size={20}
          className={isConnected ? 'text-green-500 animate-spin' : 'text-gray-400'}
          style={{ animationDuration: '6s' }}
        />
      </div>

      {/* Avatars des participants actifs */}
      {activeUsers.length > 0 && (
        <div
          className="relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {/* Groupe d'avatars empilés */}
          <div className="flex items-center" style={{ gap: '-6px' }}>
            {/* User courant en premier */}
            {currentUser && (
              <div style={{ marginRight: '-6px', zIndex: 10 }}>
                <UserAvatar user={currentUser} isCurrentUser size={35} />
              </div>
            )}

            {/* Autres utilisateurs */}
            {visibleUsers.map((user, idx) => (
              <div
                key={user.user_id}
                style={{ marginRight: idx < visibleUsers.length - 1 ? '-6px' : 0, zIndex: 9 - idx }}
              >
                <UserAvatar user={user} size={30} />
              </div>
            ))}

            {/* Badge overflow */}
            {overflowCount > 0 && (
              <div
                className="rounded-full flex items-center justify-center text-white text-xs font-bold bg-gray-400"
                style={{
                  width: 30,
                  height: 30,
                  border: '2px solid white',
                  fontSize: 11,
                  marginLeft: 2,
                }}
              >
                +{overflowCount}
              </div>
            )}
          </div>

          {/* Tooltip liste des participants */}
          {showTooltip && activeUsers.length > 0 && (
            <div
              className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
              style={{ minWidth: 200 }}
            >
              <div className="px-3 pb-2 border-b border-gray-100 mb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {activeUsers.length} {t('features.swimlane.participantsOnline')}
                </span>
              </div>
              {activeUsers.map(user => (
                <div key={user.user_id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50">
                  <UserAvatar user={user} isCurrentUser={user.user_id === currentUserId} size={26} />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-gray-800 truncate">
                      {user.fullname}
                      {user.user_id === currentUserId && (
                        <span className="ml-1 text-xs text-gray-400">({t('words.you') || 'vous'})</span>
                      )}
                    </span>
                    {user.email && (
                      <span className="text-xs text-gray-400 truncate">{user.email}</span>
                    )}
                  </div>
                  {/* Point vert "en ligne" */}
                  <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 ml-auto" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActiveUsersBar;
